import React, { useState, useCallback } from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Slide,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import type { UseProjectChatResult } from "./useProjectChat";

// Allowed file types
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  chat: UseProjectChatResult;
  projectName?: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  chat,
  projectName,
}) => {
  const {
    messages,
    loading,
    error,
    users,
    currentUserId,
    replyingTo,
    setReplyingTo,
    editingMessage,
    setEditingMessage,
    pendingAttachments,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    addAttachment,
    removeAttachment,
    loadMore,
    hasMore,
    refetch,
  } = chat;

  const [dragOver, setDragOver] = useState(false);

  const validateAndAddFile = useCallback(
    (file: File) => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert(`File type not allowed. Supported types: JPG, PNG, GIF, WebP, PDF`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large. Maximum size: 25MB`);
        return;
      }
      addAttachment(file);
    },
    [addAttachment]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const files = e.dataTransfer.files;
      Array.from(files).forEach(validateAndAddFile);
    },
    [validateAndAddFile]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragOver to false if leaving the panel entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOver(false);
    }
  };

  return (
    <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          position: "fixed",
          bottom: 80,
          right: 24,
          width: 400,
          height: 500,
          maxHeight: "calc(100vh - 100px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          zIndex: 1300,
          "@media print": {
            display: "none !important",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Project Chat
            </Typography>
            {projectName && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {projectName}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={refetch}
              disabled={loading}
              sx={{ color: "inherit" }}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton size="small" onClick={onClose} sx={{ color: "inherit" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Error state */}
        {error && (
          <Box
            sx={{
              p: 2,
              bgcolor: "error.light",
              color: "error.contrastText",
            }}
          >
            <Typography variant="body2">
              Failed to load messages. Please try again.
            </Typography>
          </Box>
        )}

        {/* Message list */}
        <MessageList
          messages={messages}
          loading={loading}
          currentUserId={currentUserId}
          users={users}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onReply={setReplyingTo}
          onEdit={setEditingMessage}
          onDelete={deleteMessage}
          onToggleReaction={toggleReaction}
        />

        {/* Input */}
        <MessageInput
          onSend={sendMessage}
          onEdit={editMessage}
          replyingTo={replyingTo}
          editingMessage={editingMessage}
          onCancelReply={() => setReplyingTo(null)}
          onCancelEdit={() => setEditingMessage(null)}
          pendingAttachments={pendingAttachments}
          onAddAttachment={addAttachment}
          onRemoveAttachment={removeAttachment}
          users={users}
          disabled={!!error}
        />

        {/* Drag overlay */}
        {dragOver && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(25, 118, 210, 0.15)",
              border: "3px dashed",
              borderColor: "primary.main",
              borderRadius: 2,
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <Typography variant="h6" color="primary" fontWeight={600}>
              Drop files here
            </Typography>
          </Box>
        )}
      </Paper>
    </Slide>
  );
};

export default ChatPanel;
